import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import {
  useMonthSchedule,
  useDaySchedule,
  useUpdateDaySchedule,
  useApplyDefaults,
} from '@/v2/hooks/settings/useSettingsSchedule'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem 1rem 20rem 1rem;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const MonthTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.875rem;

  &:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }

  svg {
    width: 1rem;
    height: 1rem;
  }
`

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-weight: 500;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
  font-size: 0.875rem;
  background-color: #2563eb;
  color: white;
  border-color: #2563eb;

  &:hover {
    background-color: #1d4ed8;
  }
`

const CalendarGrid = styled.div`
  background: white;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`

const CalendarHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`

const DayHeader = styled.div`
  padding: 0.75rem;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const CalendarBody = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
`

const DayCell = styled.div`
  min-height: 120px;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative;
  padding: 0.5rem;
  
  &:nth-child(7n) {
    border-right: none;
  }
  
  &:hover {
    background-color: #f9fafb;
  }
  
  ${props =>
    props.isToday &&
    `
    background-color: #eff6ff;
    
    &:hover {
      background-color: #dbeafe;
    }
  `}
  
  ${props =>
    props.isOtherMonth &&
    `
    background-color: #f9fafb;
    color: #9ca3af;
    
    &:hover {
      background-color: #f3f4f6;
    }
  `}
  
  ${props =>
    props.isDayOff &&
    `
    background-color: #fef2f2;
    
    &:hover {
      background-color: #fecaca;
    }
  `}
`

const DayNumber = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`

const EventsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const Event = styled.div`
  background: ${props => {
    switch (props.type) {
      case 'clinic':
        return '#dbeafe'
      case 'doctor':
        return '#d1fae5'
      default:
        return '#f3f4f6'
    }
  }};
  border: 1px solid
    ${props => {
      switch (props.type) {
        case 'clinic':
          return '#3b82f6'
        case 'doctor':
          return '#10b981'
        default:
          return '#d1d5db'
      }
    }};
  border-radius: 0.25rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
`

const EventTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.125rem;
`

const EventTime = styled.div`
  font-size: 0.625rem;
  color: #6b7280;
`

const DayOffIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  color: #dc2626;

  svg {
    width: 2rem;
    height: 2rem;
  }
`

const NotesIndicator = styled.div`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  width: 0.5rem;
  height: 0.5rem;
  background-color: #f59e0b;
  border-radius: 50%;
`

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: 0;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
`

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #374151;
  }
`

const ModalBody = styled.div`
  padding: 1.5rem;
`

const ScheduleForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const ScheduleSection = styled.div`
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: ${props => (props.isDisabled ? '#f9fafb' : 'white')};
  opacity: ${props => (props.isDisabled ? 0.6 : 1)};
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`

const SectionTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`

const Toggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const ToggleSwitch = styled.input`
  width: 2.5rem;
  height: 1.25rem;
  background-color: ${props => (props.checked ? '#2563eb' : '#d1d5db')};
  border-radius: 9999px;
  position: relative;
  appearance: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:before {
    content: '';
    position: absolute;
    top: 0.125rem;
    left: ${props => (props.checked ? '1.375rem' : '0.125rem')};
    width: 1rem;
    height: 1rem;
    background: white;
    border-radius: 50%;
    transition: left 0.2s;
  }
`

const ToggleLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
`

const TimeFields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  &:disabled {
    background-color: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
  }
`

const Checkbox = styled.input`
  margin-right: 0.5rem;
`

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  margin-bottom: 1rem;
`

const ModalFooter = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FooterNote = styled.small`
  color: #6b7280;
  font-size: 0.75rem;
`

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const formatTimeForInput = timeString => {
  if (!timeString) return ''
  const date = new Date(timeString)
  return date.toTimeString().slice(0, 5) // HH:MM format
}

const mapDayDataToForm = data => ({
  clinic: {
    isOpen: data.clinicDayHour.isOpen,
    opensAt: formatTimeForInput(data.clinicDayHour.opensAt),
    closesAt: formatTimeForInput(data.clinicDayHour.closesAt),
    hasBreak: data.clinicDayHour.hasBreak,
    breakFrom: formatTimeForInput(data.clinicDayHour.breakFrom),
    breakTo: formatTimeForInput(data.clinicDayHour.breakTo),
    notes: data.clinicDayHour.notes || '',
  },
  doctors: data.doctorsHours.map(doctor => ({
    id: doctor.id,
    doctorId: doctor.doctorId,
    name: doctor.doctorName,
    isOpen: doctor.isOpen,
    opensAt: formatTimeForInput(doctor.opensAt),
    closesAt: formatTimeForInput(doctor.closesAt),
    hasBreak: doctor.hasBreak,
    breakFrom: formatTimeForInput(doctor.breakFrom),
    breakTo: formatTimeForInput(doctor.breakTo),
    notes: doctor.notes || '',
  })),
})

const Schedule = () => {
  const navigate = useNavigate()
  const { onOpenSnackbar } = useSnackbarContext()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDay, setSelectedDay] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [selectedDateString, setSelectedDateString] = useState(null)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  const { monthData, isLoading: loading } = useMonthSchedule(year, month + 1)
  const { dayData: selectedDayData } = useDaySchedule(selectedDateString)
  const { updateDaySchedule, isPending: saving } = useUpdateDaySchedule()
  const { applyDefaults: applyDefaultsMutation } = useApplyDefaults()

  const [formData, setFormData] = useState({
    clinic: {
      isOpen: true,
      opensAt: '09:00',
      closesAt: '17:00',
      hasBreak: true,
      breakFrom: '12:00',
      breakTo: '13:00',
      notes: '',
    },
    doctors: [],
  })

  useEffect(() => {
    if (selectedDayData) {
      setFormData(mapDayDataToForm(selectedDayData))
    }
  }, [selectedDayData])

  const handleApplyDefaults = async date => {
    try {
      const dateString = date.toISOString().split('T')[0]
      await applyDefaultsMutation(dateString)
      setSelectedDateString(dateString)
      onOpenSnackbar('Default hours applied successfully', 'success')
    } catch (error) {
      onOpenSnackbar('Failed to apply default hours', 'error')
    }
  }

  const navigateMonth = direction => {
    const newDate = new Date(currentDate)
    newDate.setMonth(month + direction)
    setCurrentDate(newDate)
  }

  const getDaysArray = () => {
    if (!monthData) return []

    const days = []

    // Add leading days from previous month
    monthData.leadingDays?.forEach(day => {
      const prevMonth = month === 0 ? 11 : month - 1
      const prevYear = month === 0 ? year - 1 : year
      days.push({
        day,
        isOtherMonth: true,
        date: new Date(prevYear, prevMonth, day),
      })
    })

    // Add current month days
    monthData.days?.forEach(dayData => {
      const date = new Date(dayData.date)
      days.push({
        day: parseInt(dayData.monthDay),
        isOtherMonth: false,
        isToday: dayData.isToday,
        isDayOff: !dayData.clinicHours?.isOpen,
        hasNotes: dayData.hasNotes,
        date,
        clinicHours: dayData.clinicHours,
        doctorsDay: dayData.doctorsDay,
      })
    })

    // Add trailing days from next month
    monthData.trailingDays?.forEach((day, index) => {
      const nextMonth = month === 11 ? 0 : month + 1
      const nextYear = month === 11 ? year + 1 : year
      const dayNum = typeof day === 'string' ? parseInt(day) : day
      days.push({
        day: dayNum,
        isOtherMonth: true,
        date: new Date(nextYear, nextMonth, dayNum),
      })
    })

    return days
  }

  const handleDayClick = dayData => {
    if (dayData.isOtherMonth) return

    setSelectedDay(dayData)
    setShowModal(true)
    setSelectedDateString(dayData.date.toISOString().split('T')[0])
  }

  const handleToggleChange = (type, id, field, value) => {
    if (type === 'clinic') {
      setFormData(prev => ({
        ...prev,
        clinic: { ...prev.clinic, [field]: value },
      }))
    } else if (type === 'doctor') {
      setFormData(prev => ({
        ...prev,
        doctors: prev.doctors.map(doctor =>
          doctor.id === id ? { ...doctor, [field]: value } : doctor
        ),
      }))
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!selectedDay) return

    const dateString = selectedDay.date.toISOString().split('T')[0]

    const daySchedule = {
      clinicDayHour: {
        isOpen: formData.clinic.isOpen,
        opensAt: formData.clinic.opensAt,
        closesAt: formData.clinic.closesAt,
        hasBreak: formData.clinic.hasBreak,
        breakFrom: formData.clinic.hasBreak ? formData.clinic.breakFrom : null,
        breakTo: formData.clinic.hasBreak ? formData.clinic.breakTo : null,
        notes: formData.clinic.notes,
      },
      doctorsDayHours: {},
    }

    formData.doctors.forEach(doctor => {
      daySchedule.doctorsDayHours[doctor.id] = {
        isOpen: doctor.isOpen,
        opensAt: doctor.opensAt,
        closesAt: doctor.closesAt,
        hasBreak: doctor.hasBreak,
        breakFrom: doctor.hasBreak ? doctor.breakFrom : null,
        breakTo: doctor.hasBreak ? doctor.breakTo : null,
        notes: doctor.notes,
      }
    })

    try {
      await updateDaySchedule({ dateString, daySchedule })
      setShowModal(false)
    } catch (error) {
      onOpenSnackbar(`Error saving schedule: ${error.message}`, 'error')
    }
  }

  const renderDayEvents = dayData => {
    // Show day off if clinic is closed
    if (dayData.isDayOff || !dayData.clinicHours?.isOpen) {
      return (
        <DayOffIcon>
          <svg fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-4h2v2h-2zm0-8h2v6h-2z" />
          </svg>
        </DayOffIcon>
      )
    }

    return (
      <EventsList>
        {/* Clinic Hours */}
        {dayData.clinicHours?.isOpen && (
          <Event type="clinic">
            <EventTitle>Clinic</EventTitle>
            <EventTime>{dayData.clinicHours.openTimeString}</EventTime>
            {dayData.clinicHours.hasBreak &&
              dayData.clinicHours.breakTimeString && (
                <EventTime>
                  Break: {dayData.clinicHours.breakTimeString}
                </EventTime>
              )}
          </Event>
        )}

        {/* Doctor Hours */}
        {dayData.doctorsDay?.map(
          doctorDay =>
            doctorDay.hours?.isOpen && (
              <Event key={doctorDay.doctor.id} type="doctor">
                <EventTitle>{doctorDay.doctor.fullName}</EventTitle>
                <EventTime>{doctorDay.hours.openTimeString}</EventTime>
                {doctorDay.hours.hasBreak &&
                  doctorDay.hours.breakTimeString && (
                    <EventTime>
                      Break: {doctorDay.hours.breakTimeString}
                    </EventTime>
                  )}
              </Event>
            )
        )}
      </EventsList>
    )
  }

  const formatSelectedDate = date => {
    if (!date) return ''

    if (selectedDayData?.formattedDate) {
      return selectedDayData.formattedDate
    }

    const day = date.getDate()
    const month = MONTHS[date.getMonth()]
    const year = date.getFullYear()
    const dayName = DAYS_OF_WEEK[date.getDay()]

    const ordinal = n => {
      const s = ['th', 'st', 'nd', 'rd']
      const v = n % 100
      return n + (s[(v - 20) % 10] || s[v] || s[0])
    }

    return `${month} ${ordinal(day)}, ${year}, ${dayName}`
  }

  if (loading) {
    return (
      <Container>
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
          Loading schedule...
        </div>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <MonthTitle>
            {monthData?.monthTitle || `${MONTHS[month]} ${year}`}
          </MonthTitle>
          <NavButton onClick={() => navigateMonth(-1)} disabled={loading}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Previous month
          </NavButton>
          <NavButton onClick={() => navigateMonth(1)} disabled={loading}>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
            Next month
          </NavButton>
        </HeaderLeft>
        <Button onClick={() => navigate('/v2/settings/regular-hours')}>
          Edit clinic's regular hours
        </Button>
      </Header>

      <CalendarGrid>
        <CalendarHeader>
          {DAYS_OF_WEEK.map(day => (
            <DayHeader key={day}>{day}</DayHeader>
          ))}
        </CalendarHeader>

        <CalendarBody>
          {getDaysArray().map((dayData, index) => (
            <DayCell
              key={index}
              isToday={dayData.isToday}
              isOtherMonth={dayData.isOtherMonth}
              isDayOff={dayData.isDayOff}
              onClick={() => handleDayClick(dayData)}
            >
              <DayNumber>{dayData.day}</DayNumber>
              {!dayData.isOtherMonth && renderDayEvents(dayData)}
              {!dayData.isOtherMonth && dayData.hasNotes && <NotesIndicator />}
            </DayCell>
          ))}
        </CalendarBody>
      </CalendarGrid>

      {showModal && selectedDay && (
        <Modal
          onClick={e => e.target === e.currentTarget && setShowModal(false)}
        >
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{formatSelectedDate(selectedDay.date)}</ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
            </ModalHeader>

            <ModalBody>
              <ScheduleForm onSubmit={handleSubmit}>
                {/* Clinic Schedule */}
                <ScheduleSection isDisabled={!formData.clinic.isOpen}>
                  <SectionHeader>
                    <SectionTitle>Clinic</SectionTitle>
                    <Toggle>
                      <ToggleLabel>On</ToggleLabel>
                      <ToggleSwitch
                        type="checkbox"
                        checked={formData.clinic.isOpen}
                        onChange={e =>
                          handleToggleChange(
                            'clinic',
                            null,
                            'isOpen',
                            e.target.checked
                          )
                        }
                      />
                    </Toggle>
                  </SectionHeader>

                  <TimeFields>
                    <FieldGroup>
                      <Label>Opens at</Label>
                      <Input
                        type="time"
                        value={formData.clinic.opensAt}
                        disabled={!formData.clinic.isOpen}
                        onChange={e =>
                          handleToggleChange(
                            'clinic',
                            null,
                            'opensAt',
                            e.target.value
                          )
                        }
                      />
                    </FieldGroup>
                    <FieldGroup>
                      <Label>Closes at</Label>
                      <Input
                        type="time"
                        value={formData.clinic.closesAt}
                        disabled={!formData.clinic.isOpen}
                        onChange={e =>
                          handleToggleChange(
                            'clinic',
                            null,
                            'closesAt',
                            e.target.value
                          )
                        }
                      />
                    </FieldGroup>
                  </TimeFields>

                  <CheckboxLabel>
                    <Checkbox
                      type="checkbox"
                      checked={formData.clinic.hasBreak}
                      disabled={!formData.clinic.isOpen}
                      onChange={e =>
                        handleToggleChange(
                          'clinic',
                          null,
                          'hasBreak',
                          e.target.checked
                        )
                      }
                    />
                    Break?
                  </CheckboxLabel>

                  {formData.clinic.hasBreak && (
                    <TimeFields>
                      <FieldGroup>
                        <Label>From</Label>
                        <Input
                          type="time"
                          value={formData.clinic.breakFrom}
                          disabled={!formData.clinic.isOpen}
                          onChange={e =>
                            handleToggleChange(
                              'clinic',
                              null,
                              'breakFrom',
                              e.target.value
                            )
                          }
                        />
                      </FieldGroup>
                      <FieldGroup>
                        <Label>To</Label>
                        <Input
                          type="time"
                          value={formData.clinic.breakTo}
                          disabled={!formData.clinic.isOpen}
                          onChange={e =>
                            handleToggleChange(
                              'clinic',
                              null,
                              'breakTo',
                              e.target.value
                            )
                          }
                        />
                      </FieldGroup>
                    </TimeFields>
                  )}
                </ScheduleSection>

                {/* Doctor Schedules */}
                {formData.doctors.map(doctor => (
                  <ScheduleSection key={doctor.id} isDisabled={!doctor.isOpen}>
                    <SectionHeader>
                      <SectionTitle>{doctor.name}</SectionTitle>
                      <Toggle>
                        <ToggleLabel>On</ToggleLabel>
                        <ToggleSwitch
                          type="checkbox"
                          checked={doctor.isOpen}
                          onChange={e =>
                            handleToggleChange(
                              'doctor',
                              doctor.id,
                              'isOpen',
                              e.target.checked
                            )
                          }
                        />
                      </Toggle>
                    </SectionHeader>

                    <TimeFields>
                      <FieldGroup>
                        <Label>Opens at</Label>
                        <Input
                          type="time"
                          value={doctor.opensAt}
                          disabled={!doctor.isOpen}
                          onChange={e =>
                            handleToggleChange(
                              'doctor',
                              doctor.id,
                              'opensAt',
                              e.target.value
                            )
                          }
                        />
                      </FieldGroup>
                      <FieldGroup>
                        <Label>Closes at</Label>
                        <Input
                          type="time"
                          value={doctor.closesAt}
                          disabled={!doctor.isOpen}
                          onChange={e =>
                            handleToggleChange(
                              'doctor',
                              doctor.id,
                              'closesAt',
                              e.target.value
                            )
                          }
                        />
                      </FieldGroup>
                    </TimeFields>

                    <CheckboxLabel>
                      <Checkbox
                        type="checkbox"
                        checked={doctor.hasBreak}
                        disabled={!doctor.isOpen}
                        onChange={e =>
                          handleToggleChange(
                            'doctor',
                            doctor.id,
                            'hasBreak',
                            e.target.checked
                          )
                        }
                      />
                      Break?
                    </CheckboxLabel>

                    {doctor.hasBreak && (
                      <TimeFields>
                        <FieldGroup>
                          <Label>From</Label>
                          <Input
                            type="time"
                            value={doctor.breakFrom}
                            disabled={!doctor.isOpen}
                            onChange={e =>
                              handleToggleChange(
                                'doctor',
                                doctor.id,
                                'breakFrom',
                                e.target.value
                              )
                            }
                          />
                        </FieldGroup>
                        <FieldGroup>
                          <Label>To</Label>
                          <Input
                            type="time"
                            value={doctor.breakTo}
                            disabled={!doctor.isOpen}
                            onChange={e =>
                              handleToggleChange(
                                'doctor',
                                doctor.id,
                                'breakTo',
                                e.target.value
                              )
                            }
                          />
                        </FieldGroup>
                      </TimeFields>
                    )}
                  </ScheduleSection>
                ))}

                {/* Notes */}
                <FieldGroup>
                  <Label>Notes</Label>
                  <Input
                    type="text"
                    value={formData.clinic.notes}
                    onChange={e =>
                      handleToggleChange(
                        'clinic',
                        null,
                        'notes',
                        e.target.value
                      )
                    }
                    placeholder="Add any notes for this day..."
                  />
                </FieldGroup>
              </ScheduleForm>
            </ModalBody>

            <ModalFooter>
              <FooterNote>
                Settings for this day will overwrite regular hours
              </FooterNote>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button
                  onClick={() => handleApplyDefaults(selectedDay.date)}
                  disabled={saving}
                >
                  Apply Defaults
                </Button>
                <Button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={saving}
                  variant="primary"
                >
                  {saving ? 'Saving...' : 'Update'}
                </Button>
              </div>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Container>
  )
}

export default Schedule
